import { KeyRound, Mail, Fingerprint, Shield, Key, Smartphone, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthMethod, AvailableAuthMethod } from "@/types/state";

interface AuthMethodSelectorProps {
  stepNumber: 1 | 2;
  availableMethods: AuthMethod[];
  selectedMethod: AuthMethod | null;
  onSelectMethod: (method: AuthMethod) => void;
  excludeMethod?: AuthMethod;
  userConfiguredMethods?: AvailableAuthMethod[];
  loading?: boolean;
  onBack?: () => void;
  email?: string;
}

const methodIcons: Record<AuthMethod, React.ReactNode> = {
  password: <KeyRound className="h-5 w-5" />,
  magic_link: <Mail className="h-5 w-5" />,
  totp: <Smartphone className="h-5 w-5" />,
  backup_code: <Shield className="h-5 w-5" />,
  passkey: <Fingerprint className="h-5 w-5" />,
  security_key: <Key className="h-5 w-5" />,
  webauthn_biometric: <Shield className="h-5 w-5" />,
};

const methodLabels: Record<AuthMethod, string> = {
  password: "Password",
  magic_link: "Magic Link",
  totp: "Authenticator App",
  backup_code: "Backup Code",
  passkey: "Passkey",
  security_key: "Security Key",
  webauthn_biometric: "Biometric",
};

const methodDescriptions: Record<AuthMethod, string> = {
  password: "Use your password",
  magic_link: "Get a link sent to your email",
  totp: "Use your authenticator app code",
  backup_code: "Use a backup recovery code",
  passkey: "Use a saved passkey",
  security_key: "Use a hardware security key",
  webauthn_biometric: "Use fingerprint or face recognition",
};

export const AuthMethodSelector = ({
  stepNumber,
  availableMethods,
  selectedMethod,
  onSelectMethod,
  excludeMethod,
  userConfiguredMethods = [],
  loading = false,
  onBack,
  email,
}: AuthMethodSelectorProps) => {
  // Filter out excluded method and only show methods user has configured (plus password and magic link which are always available)
  const filteredMethods = availableMethods.filter((method) => {
    if (method === excludeMethod) return false;
    
    // Password and magic link are always available for method 1
    if (stepNumber === 1 && (method === 'password' || method === 'magic_link')) {
      return true;
    }
    
    // For method 2, only show methods the user has configured
    // Special case: if user has TOTP, also allow backup codes
    if (stepNumber === 2) {
      if (method === 'backup_code') {
        return userConfiguredMethods.some((m) => m.method_type === 'totp');
      }
      return userConfiguredMethods.some((m) => m.method_type === method);
    }
    
    // For method 1, show configured methods plus password and magic link
    return userConfiguredMethods.some((m) => m.method_type === method) || 
           method === 'password' || 
           method === 'magic_link';
  });

  const getPrimaryMethod = () => {
    const primary = userConfiguredMethods.find((m) => m.is_primary);
    return primary?.method_type;
  };

  const primaryMethod = getPrimaryMethod();

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          {stepNumber === 1 ? "Choose Your Sign-In Method" : "Choose Second Factor"}
        </CardTitle>
        <CardDescription>
          {stepNumber === 1
            ? email ? `Signing in as ${email}` : "Select how you'd like to sign in"
            : "For added security, verify with a different method"}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {filteredMethods.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No additional authentication methods configured.</p>
            <p className="text-sm mt-2">
              Set up more methods in your profile settings for enhanced security.
            </p>
          </div>
        ) : (
          filteredMethods.map((method) => {
            const isPrimary = method === primaryMethod;
            const userMethod = userConfiguredMethods.find((m) => m.method_type === method);
            
            return (
              <Button
                key={method}
                variant={selectedMethod === method ? "default" : "outline"}
                className="w-full justify-start h-auto py-4 px-4"
                onClick={() => onSelectMethod(method)}
                disabled={loading}
              >
                <div className="flex items-start gap-3 w-full">
                  <div className="mt-0.5">{methodIcons[method]}</div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{methodLabels[method]}</span>
                      {isPrimary && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          Primary
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {userMethod?.credential_name || methodDescriptions[method]}
                    </p>
                    {userMethod?.last_used_at && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last used: {new Date(userMethod.last_used_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              </Button>
            );
          })
        )}
        {onBack && stepNumber === 1 && (
          <Button
            type="button"
            variant="ghost"
            onClick={onBack}
            className="w-full mt-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Change Email
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
